import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { PaymentAmountFields } from "./PaymentAmountFields";
import { usePaymentForm } from "../hooks/usePaymentForm";
import {
  blankPaymentForm,
  hasPaymentAmount,
  paymentPayload,
  settlementTotal,
} from "../lib/paymentForm";
import api, { formatApiError } from "../lib/api";

const PAYMENT_FIELD_IDS = {
  interestId: "pay-interest",
  interestTestId: "payment-interest-input",
  penaltyId: "pay-penalty",
  penaltyTestId: "payment-penalty-input",
  mainDebtId: "pay-main-debt",
  mainDebtTestId: "payment-main-debt-input",
  referralId: "pay-referral",
  referralTestId: "payment-referral-input",
  generateTestId: "generate-payment-remark-button",
  noteId: "pay-remark",
  noteTestId: "payment-remark-input",
  dateId: "pay-date",
  dateTestId: "payment-date-input",
  requiresPaymentDate: true,
  submitLabel: "✓ Paid",
  submitTestId: "record-detailed-payment-button",
};

export default function RecordPaymentDialogV2({ ledger, open, onClose, onDone }) {
  const paymentForm = usePaymentForm(blankPaymentForm());
  const [saving, setSaving] = useState(false);
  const [settling, setSettling] = useState(false);
  const { form, generate, generated, replace, update } = paymentForm;

  useEffect(() => {
    if (open) {
      replace(blankPaymentForm());
    }
  }, [ledger?.loan.id, open, replace]);

  const pay = async (event) => {
    event.preventDefault();
    if (!generated) {
      toast.error("Generate Payment before recording it.");
      return;
    }
    if (!hasPaymentAmount(form)) {
      toast.error("Enter at least one payment amount.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/loans/${ledger.loan.id}/repayments`, paymentPayload(form));
      toast.success("Payment recorded");
      onDone();
      onClose();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const settle = async () => {
    const settlementAmount = settlementTotal(form);
    if (!form.settle_remark || !form.settled_date || settlementAmount <= 0) {
      toast.error("Enter Main Debt or Interest, then select the settlement date and remark.");
      return;
    }
    setSettling(true);
    try {
      await api.post(`/loans/${ledger.loan.id}/settle`, {
        settle_remark: form.settle_remark,
        settled_date: form.settled_date,
        main_debt: Number(form.main_debt || 0),
        interest_amount: Number(form.interest_amount || 0),
        penalty_amount: Number(form.penalty_amount || 0),
        referral_commission: Number(form.referral_commission || 0),
      });
      toast.success("Loan settled and recorded");
      onDone();
      onClose();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSettling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="record-payment-dialog" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            Record the payment breakdown or settle the remaining loan balance.
          </DialogDescription>
        </DialogHeader>
        <form className="record-payment-form" onSubmit={pay}>
          <PaymentAmountFields
            config={PAYMENT_FIELD_IDS}
            form={form}
            generated={generated}
            onChange={update}
            onGenerate={generate}
            saving={saving}
          />
          <div className="settle-loan-section">
            <div className="settlement-total" data-testid="settlement-total-value">
              <span>Settlement Total</span>
              <strong>RM {settlementTotal(form).toFixed(2)}</strong>
            </div>
            <div>
              <Label htmlFor="settle-remark">Settle Loan Remark</Label>
              <Textarea
                id="settle-remark"
                data-testid="settle-loan-remark-input"
                value={form.settle_remark}
                onChange={(event) => update("settle_remark", event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="settle-date">Settled Date</Label>
              <Input
                id="settle-date"
                data-testid="settle-loan-date-input"
                type="date"
                value={form.settled_date}
                onChange={(event) => update("settled_date", event.target.value)}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={settle}
              disabled={(
                settling
                || settlementTotal(form) <= 0
                || !form.settle_remark
                || !form.settled_date
              )}
              data-testid="settle-loan-button"
            >
              {settling ? "Settling…" : "✓ Loan Settle?"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}